// Realm-trigger firing + prompt stack + site-play effects, extracted
// from GameBoard.jsx. fireRealmTrigger and its siblings walk the
// watcher tables built by engine/automation (findRealmTriggerWatchers,
// collectOn*Firings) and either resolve silent ops directly or enqueue
// storyline prompts for player choices. applySitePlayEffects runs the
// affinity + mana recipe when a site lands; the prompt-stack helpers
// own the ordered, seat-filtered prompt queue and its rehydration on
// session restore.

import { UI } from '../../../utils/arena/uiSounds';
import { OP, TRIGGER, evaluateCondition, findRealmTriggerWatchers, isDisabled, partitionByPriority } from '../../../utils/game/engine/automation';
import { dispatchSilentOp, SILENT_OP_SET } from '../../../utils/game/engine/automation/ops/silentOps';
import { collectOnEdgeTraverseFirings } from '../../../utils/game/engine/automation/triggers/onEdgeTraverse';
import { collectOnEnterLocationFirings } from '../../../utils/game/engine/automation/triggers/onEnterLocation';
import { anchorCoveredCells, chebyshevDistance, countsAsMinion, effectivePower, effectiveSiteThresholds, isLandSiteAtCell, isWaterSiteAtCell, placementChoicesForPlay, pushEvent, pushSimultaneous, resetCardOnLeavePlay } from '../../../utils/game/engine/core';
import { isCardSilenced } from '../../../utils/game/engine/core/keywordQuery';
import { SERVER_SILENT_OPS, SERVER_AUTONOMOUS_TRIGGERS, serverOwnsOnDrawEntry } from '../../../utils/game/net/automationIntents';
import EffectPromptDrawer from '../EffectPromptDrawer';
import { k, t } from '../../../localization/t';
import { msg } from '../../../utils/game/logMessage';

// Combat-trigger effects that resolve inline, with no storyline
// confirm. Shared by the self path (GameBoard._fireCombatTrigger) and
// the watcher pass (fireCombatWatcherTrigger below); resolution lives
// in GameBoard._resolveCombatSilentEffects.
export const COMBAT_SILENT_OPS = new Set([
  OP.HEAL_PLAYER, OP.CONVERT_WATCH_TARGET_TO_RUBBLE,
  OP.GRANT_STAT_SELF_THIS_TURN, OP.MARK_USED_THIS_TURN,
  OP.APPLY_STATUS_TO_SELF, OP.INTERROGATOR_DODGE,
  OP.DISPEL_SELF, OP.DISCARD_SPELL,
]);

/**
 * Enqueue a prompt for each of a card's automation entries that
 * fires on the given trigger.
 *
 * The prompt-confirmation gate keeps the player in control: nothing
 * is added to the ledger or broadcast until the player clicks
 * Confirm in the EffectPromptDrawer. Cancel discards the prompt and
 * the player can apply the effect manually via the existing tracker
 * controls.
 *
 * Static (continuous) entries are also enqueued so the player sees
 * them on play; if they Cancel, the aura simply doesn't take effect
 * until they re-apply manually. Same opt-in semantics as triggered
 * effects.
 *
 * Reminder-op entries surface as their own prompts (`promptType:
 * 'reminder'`) so complex / out-of-scope rules text is at least
 * acknowledged visibly.
 */
// v1.78. Storyline mutation gateway. Every push into pendingPrompts
// routes through these so codex resolution rules (LIFO, simultaneous
// active/non-active priority, source-order intent) live in one place
// backed by the pure storyline.js helpers.
//
// v1.105. Generic critical-prompt persistence layer. The runtime
// state fields stay the source of truth for what the UI renders;
// this stack is a JSON-safe projection that survives the autosave
// round-trip so a modal that vanished with the browser tab (or a
// disconnected peer) comes back on reconnect instead of leaving the
// game wedged. Every place that opens a critical prompt calls
// _openPrompt; every closer calls _closePrompt(id). _rehydratePromptStack
// re-fires each descriptor's opener on restore.
//
// Descriptor shape: { id, kind, controller, payload }. All JSON-safe.
//   - id: monotonic stable identifier so close-by-id is unambiguous
//   - kind: which prompt type (drives the rehydrate switch)
//   - controller: 'p1' | 'p2'. Owner; in MP only the matching seat
//     rehydrates, in sandbox both seats rehydrate from one snapshot
//   - payload: kind-specific JSON (cardIds not cardInstances, arrays
//     not Sets, no closures)
export function openPrompt(host, descriptor) {
  if (!descriptor || !descriptor.id || !descriptor.kind) return;
  host.setState((s) => ({
    pendingPromptStack: [
      ...(s.pendingPromptStack || []).filter((d) => d.id !== descriptor.id),
      descriptor,
    ],
  }), host.scheduleLocalSave);
}

export function closePrompt(host, id) {
  if (!id) return;
  host.setState((s) => {
    const next = (s.pendingPromptStack || []).filter((d) => d.id !== id);
    if (next.length === (s.pendingPromptStack || []).length) return null;
    return { pendingPromptStack: next };
  }, host.scheduleLocalSave);
}

// Walk the persisted stack on restore and re-fire each opener so the
// matching runtime state field gets re-populated. Idempotent: each
// rehydrator must tolerate being called on a fresh restored client
// where the runtime field is empty. Unknown kinds are silently
// skipped so a newer save loaded by an older client doesn't crash.
export function rehydratePromptStack(host, descriptors) {
  if (!Array.isArray(descriptors) || descriptors.length === 0) return;
  for (const d of descriptors) {
    if (!d || !d.kind) continue;
    // MP gate: only rehydrate prompts that belong to the local seat.
    // In sandbox the local player runs both seats so everything
    // rehydrates. Without this gate a guest would re-open the host's
    // private prompts on reconnect.
    if (!host.props.isCasualSandbox && d.controller) {
      const localPlayer = host.state.isHost ? 'p1' : 'p2';
      if (d.controller !== localPlayer) continue;
    }
    try {
      switch (d.kind) {
        case 'deferredDrawPrompt':
          host._pendingDrawPromptFor = d.payload?.player || null;
          break;
        case 'drawPhaseChoice':
          host._rehydrateDrawPhaseChoice(d);
          break;
        default:
          // Unknown kind. Skip silently; forward-compat.
          break;
      }
    } catch {
      // Per-descriptor isolation: one bad payload mustn't block the
      // rest of the stack from rehydrating.
    }
  }
}

// Single chokepoint for every prompt that enters the storyline.
// Drops any prompt whose `controller` doesn't match the local seat
// in MP. Sorcery's automation pipeline runs on BOTH clients in
// parallel (deck spawn, realm-entry watchers, combat triggers all
// fire locally), so without this filter the opponent's Confirm /
// Choice / target-pick popup would appear on our side too. The
// controller's client confirms and broadcasts the resulting state
// change (effect:added, trackerSet, etc.) via existing channels;
// our local mirror picks it up without ever rendering the popup.
//
// Sandbox: one player drives both seats. Pass through.
// Spectators: not a controller for either seat. Drop everything.
// Prompts with no controller field (legacy / system prompts): pass
// through so we don't accidentally swallow a global prompt.
export function filterPromptsForLocalSeat(host, prompts) {
  if (!Array.isArray(prompts) || prompts.length === 0) return [];
  if (host.props.isCasualSandbox) return prompts;
  if (host.props.isSpectating) return [];
  const localPlayer = host._viewerPlayer();
  return prompts.filter((p) => !p?.controller || p.controller === localPlayer);
}

// Opponent-side mirror of a spell cast. The caster's client broadcasts
// `storyline:cast`; here we add a READ-ONLY acknowledge entry to THIS
// player's storyline so they see what was played and clear it at their
// own pace. `controller` is set to the local seat so the entry passes
// the seat filter and renders on this side; the caster's seat only feeds
// the description. Purely informational. Confirming disposes nothing.
export function enqueueOpponentSpellNotice(host, payload) {
  if (!payload?.cardId) return;
  const localSeat = host._viewerPlayer();
  const casterLabel = payload.controller === 'p1' ? t('Player 1') : t('Player 2');
  const cardName = payload.cardName || t('a spell');
  host._pushStorylineEvents([{
    id: `ack-cast-${payload.cardId}-${localSeat}`,
    promptType: 'acknowledge',
    cardName,
    // Prefer the full mirror-derived instance (its cardId is the
    // CATALOG id) so inspecting the pending card resolves real card
    // data. The bare stub's cardId is the instance id, which the
    // inspector's catalog lookup can't resolve.
    cardInstance: payload.cardInstance
      || { id: payload.cardId, cardId: payload.cardId, imageUrl: payload.imageUrl || null },
    controller: localSeat,
    description: t('{caster} cast {card}.', { caster: casterLabel, card: cardName }),
  }]);
}

// v1.111. Opponent-side mirror of an activated ability. The activating
// client broadcasts `storyline:ability`; here we add a READ-ONLY
// acknowledge entry to THIS player's storyline (controller = local seat
// so it passes the seat filter). Purely informational. Confirming
// disposes nothing.
export function enqueueOpponentAbilityNotice(host, payload) {
  if (!payload?.cardId) return;
  const localSeat = host._viewerPlayer();
  const actorLabel = payload.controller === 'p1' ? t('Player 1') : t('Player 2');
  const cardName = payload.cardName || t('an ability');
  const desc = payload.description
    ? t('{actor} activated {card}: {description}.', { actor: actorLabel, card: cardName, description: payload.description })
    : t('{actor} activated {card}.', { actor: actorLabel, card: cardName });
  host._pushStorylineEvents([{
    id: `ack-ability-${payload.cardId}-${localSeat}`,
    promptType: 'acknowledge',
    cardName,
    cardInstance: { id: payload.cardId, cardId: payload.cardId, imageUrl: payload.imageUrl || null },
    controller: localSeat,
    description: desc,
  }]);
}

// _pushStorylineEvents: orderedPrompts are in INTENDED RESOLUTION
// ORDER. The first one is meant to resolve first. We reverse before
// appending so the LIFO stack pops them in that order.
export function pushStorylineEvents(host, orderedPrompts) {
  const local = host._filterPromptsForLocalSeat(orderedPrompts);
  if (local.length === 0) return;
  const reversed = [...local].reverse();
  host.setState((s) => {
    let next = s.pendingPrompts;
    for (const p of reversed) next = pushEvent(next, p);
    return { pendingPrompts: next };
  });
}

// _pushSimultaneousStorylineEvents: multiple cards' triggers fire
// from a single event (realm-entry watchers, end-of-turn fan-out).
// Caller passes prompts in INTENDED RESOLUTION ORDER (first-to-
// resolve first within each controller's group). The helper reverses
// each controller's group so LIFO pops in that order, then uses
// pushSimultaneous for codex active-vs-non-active priority (active
// deeper, non-active on top → non-active resolves first).
export function pushSimultaneousStorylineEvents(host, prompts, activePlayer) {
  const local = host._filterPromptsForLocalSeat(prompts);
  if (local.length === 0) return;
  const active   = local.filter((p) => p.controller === activePlayer).reverse();
  const nonActive = local.filter((p) => p.controller !== activePlayer).reverse();
  host.setState((s) => ({
    pendingPrompts: pushSimultaneous(s.pendingPrompts, [...active, ...nonActive], activePlayer),
  }));
}

// placementChoicesForPlay is engine code, whose text stays English. Its
// three region labels are marked here so the extractor collects them.
const REGION_CHOICE_LABELS = {
  Surface: k('Surface'),
  Burrowed: k('Burrowed'),
  Submerged: k('Submerged'),
};

/**
 * Fire the play-commit side effects for a freshly played site:
 * recalculate elemental affinities and, if the site produces mana,
 * bump the mana tracker and broadcast it. No-op for non-sites.
 */
// v1.30b. Enqueue a placement prompt when a Burrowing or Submerge
// minion enters a cell that supports its subsurface region. The
// prompt asks the player to pick Surface / Burrowed / Submerged.
// Vanilla minions get no prompt; the existing surface-default
// behaviour stands.
export function maybeEnqueuePlacementPrompt(host, cardInstance) {
  if (!cardInstance || cardInstance.isSite) return;
  if (!countsAsMinion(cardInstance)) return;
  // Subsurface-view drag-plays stamp card.subsurface during commit
  // (see _blockInvalidSubsurfacePlays). The player has already chosen
  // burrowed / submerged by virtue of being in that view, so the
  // Surface/Burrowed/Submerged prompt would just re-ask a decided
  // question. Skip the prompt when subsurface is already set.
  if (cardInstance.subsurface) return;
  const mesh = host.meshes.get(cardInstance.id);
  if (!mesh) return;
  const cell = host._cellAtWorld(mesh.position.x, mesh.position.z);
  if (!cell) return;
  const fullCard = host._getFullCard(cardInstance.cardId);
  const getFullCard = host._getFullCard;
  const choices = placementChoicesForPlay(
    cardInstance, fullCard, host.meshes, host._cellAtWorld, cell.col, cell.row, getFullCard,
  );
  if (choices.length === 0) return;
  const prompt = {
    id: `prompt-${cardInstance.id}-placement-${Math.random().toString(36).slice(2, 8)}`,
    cardInstance,
    cardName: fullCard?.name || cardInstance.cardId,
    controller: cardInstance.rotated ? 'p2' : 'p1',
    promptType: 'placement_choice',
    description: t('Pick how {name} enters the realm.', { name: fullCard?.name || t('this minion') }),
    choices: choices.map((c) => ({ value: c.region, label: t(REGION_CHOICE_LABELS[c.label] ?? c.label) })),
    sourceEntry: null,
    sourceEffect: null,
  };
  host._pushStorylineEvents([prompt]);
}

export function applySitePlayEffects(host, cardInstance) {
  // Automation runs for ANY card type (Bloom sites are sites, but
  // Genesis-triggered minions like Apprentice Wizard need it too in
  // future tagger passes). Helper is a no-op for cards without
  // matching entries.
  host.applyCardAutomation(cardInstance);
  // The Tower of Babel build trigger lives on the Apex's on_play
  // (condition: played onto a The Base of Babel's square). The Apex may
  // only be played atop a Base already in play, so there is no reverse
  // order to re-evaluate. The Apex's own on_play always covers the build.
  // Static auras may have changed: the played card might be an aura
  // source, OR it might be a new target for an existing source's
  // aura. Cheap board-state recompute covers both cases.
  host.recomputeStaticAuras();
  // v1.30b. On-play placement prompt for Burrowing / Submerge
  // minions. Only fires when the cell offers more than one
  // placement (surface + at least one subsurface).
  host._maybeEnqueuePlacementPrompt(cardInstance);
  if (!cardInstance.isSite) return;
  const player = cardInstance.rotated ? 'p2' : 'p1';
  host.recalculateSiteAffinities(player);
  // Site enters play → +1 mana for the controller. Auth matches: the server
  // grants it on every site → play commit (grantSiteEntryMana) and the diff
  // carries it back; emitting a grantMana intent here would double it.
  if (!host._authZonesEnabled?.() && host._siteProvidesMana(cardInstance)) {
    host.playerResources.grantMana(player, 1);
  }
}

// v1.42. Fire watcher triggers for a realm-entry or realm-exit
// event. Iterates every OTHER card on the board and enqueues their
// matching automation entries (if any). Per codex, the active
// player's watchers go deeper in the storyline stack and the
// non-active player's go on top (resolve first). Today no parser
// emits on_realm_entry / on_realm_exit entries, so this is pure
// infrastructure. When a future tagger pass tags Bottomless Pit
// (kill non-Airborne entrant) or Mariner's Curse (submerge water
// entrant), they'll fire automatically without further wiring.
//
// watchTargetCard is passed to applyCardAutomation in the
// overrides bag so the watcher's condition + effect can reference
// the entering/leaving card (a card-text feature parser support
// still has to grow).
// Fire `on_enter_location` triggers for the unit that just landed
// at `cell`. Walks every card AT the cell, looks at intrinsic
// automation entries (parser-emitted on_enter_location) plus
// `_grantedTriggers[]` installed by the static-aura recompute, and
// fires effects for entries whose condition holds.
//
// Druid pattern: a flipped Druid grants `on_enter_location → damage
// watch target 1` onto nearby allied sites. When an enemy unit
// moves into one of those sites' cells, this fires; the condition
// (`watch_target_filter`, `enemy: true`, `not_site: true`) is
// checked against (source: site, target: moving unit); damage
// routes through `_applyDamageToTarget` so Ward / replacements /
// Lethal all behave uniformly.
//
// Watch context shape mirrors the realm-trigger pipeline:
// `{ target: <entering unit> }`. Exposed to the condition checker
// via `opts.watchTarget`.
// Thin orchestrator. The trigger collector (engine module) decides
// which walls should fire `on_edge_traverse` for this move; the
// silent-ops dispatcher handles each op (damage on traverse, etc.).
export function fireOnEdgeTraverseAt(host, unit, fromCell, toCell) {
  const firings = collectOnEdgeTraverseFirings(unit, fromCell, toCell, host.meshes, host._getFullCard);
  if (firings.length === 0) return;
  for (const { card, fullCard, entries } of firings) {
    for (const entry of entries) {
      const condOk = evaluateCondition(entry.condition, card, fullCard, host.meshes, {
        cellAt: host._cellAtWorld,
        watchTarget: unit,
      });
      if (!condOk) continue;
      for (const eff of (entry.effect || [])) {
        dispatchSilentOp(eff, card, entry, host._silentOpsHost, { watchTarget: unit });
      }
    }
  }
}

// Thin orchestrator. The trigger collector (engine module) decides
// which cards' entries should fire; the silent-ops dispatcher (engine
// module) handles each op. GameBoard owns only the live state +
// helpers wired into the host adapter.
export function fireOnEnterLocationAt(host, enteringUnit, cell, reason = 'move') {
  if (!enteringUnit || !cell) return;
  // `on_stop` fires on the MOVER itself when it completes a move.
  // voluntary step, teleport, pull, or push all funnel through here
  // (pointerInteractions move completion + _moveCardToCell). Being
  // summoned is not a "stop", so the summon reason skips it.
  // applyCardAutomation owns condition gates, the optional-entry
  // confirm drawer, and silent dispatch, so Adtonitum's "may deal 3
  // damage to another random unit there" surfaces its prompt.
  if (reason !== 'summon') {
    const owner = enteringUnit.rotated ? 'p2' : 'p1';
    host.applyCardAutomation(enteringUnit, [TRIGGER.ON_STOP], { controller: owner });
  }
  const firings = collectOnEnterLocationFirings(
    enteringUnit, cell, host.meshes, host._cellAtWorld, host._getFullCard,
  );
  if (firings.length === 0) return;
  const getFullCard = host._getFullCard;
  const cellIsWaterSite = (col, row) => isWaterSiteAtCell(host.meshes, host._cellAtWorld, col, row, getFullCard);
  const cellIsLandSite  = (col, row) => isLandSiteAtCell (host.meshes, host._cellAtWorld, col, row, getFullCard);
  // The entering unit's controller is the effective controller for the
  // firing entry, NOT the source's: Vaults of Zul is an uncontrolled
  // site, but "they draw three cards and skip their next turn" targets
  // the Avatar who stopped there. Silent ops (Druid's enemy ping) read
  // the watchTarget directly and ignore this; the prompt-routed path
  // uses it so draw / skip_next_turn land on the right player.
  const enteringController = enteringUnit.rotated ? 'p2' : 'p1';
  const auth = host._authZonesEnabled?.();
  for (const { card, fullCard, entries } of firings) {
    // Auth cutover: the SERVER fires this watcher's fully-server-silent CATALOG
    // entries from the move/cast commit (movementTriggers). Watchers sitting AT
    // the entered cell AND aura watchers whose footprint covers it (the server
    // re-derives aura geometry from pos and matches anchorCoveredCells. S4).
    // Only aura-granted `_grantedTriggers` (Druid) have no server analog and
    // stay local.
    const watcherMesh = host.meshes.get(card.id);
    const watcherCell = watcherMesh
      ? host._cellAtWorld(watcherMesh.position.x, watcherMesh.position.z) : null;
    const atEnteredCell = !!watcherCell
      && watcherCell.col === cell.col && watcherCell.row === cell.row;
    const coversEnteredCell = card.type === 'Aura' && card._auraAnchor && card._auraShape
      && anchorCoveredCells(card._auraAnchor, card._auraShape.shape).includes(`${cell.col},${cell.row}`);
    for (const entry of entries) {
      const granted = (card._grantedTriggers || []).includes(entry);
      if (auth && !granted && (atEnteredCell || coversEnteredCell)
          && (entry.effect || []).length > 0
          && (entry.effect || []).every((e) => SERVER_SILENT_OPS.has(e.op))) {
        continue;
      }
      const condOk = evaluateCondition(entry.condition, card, fullCard, host.meshes, {
        cellAt: host._cellAtWorld,
        watchTarget: enteringUnit,
        enterReason: reason,
        cellIsWaterSite, cellIsLandSite,
      });
      if (!condOk) continue;
      // Entries whose every effect is silent-dispatchable resolve
      // inline (Druid / Mariner's Curse). Anything with a player-facing
      // op (Vaults' draw) routes through applyCardAutomation so the
      // confirm / draw drawer renders, then the mark_cell_visited +
      // skip_next_turn silent ops fire on confirm.
      const allSilent = (entry.effect || []).every((e) => SILENT_OP_SET.has(e.op));
      if (allSilent) {
        for (const eff of (entry.effect || [])) {
          dispatchSilentOp(eff, card, entry, host._silentOpsHost,
            { watchTarget: enteringUnit, controller: enteringController });
        }
      } else {
        host.applyCardAutomation(card, [TRIGGER.ON_ENTER_LOCATION], {
          watchTarget: enteringUnit,
          controller: enteringController,
        });
      }
    }
  }
}

// Fire `on_spell_cast` watchers after a Magic spell commits. Walks
// every card on the board and checks its catalog automation entries
// for matching triggers; `self_controller`-scoped entries fire only
// when the watcher's controller matches the spell's controller
// (Enchantress's "whenever YOU cast a spell"), `global` scope fires
// for any caster (The Immortal Throne's "whenever anyone plays a
// card"). Watch context carries the cast spell so conditions read its
// cost / subtype / destination cell.
//
// cast-watching DRAW + ADD_COUNTER entries resolve inline here
// (silently, deterministic on both clients) so the gated reward
// applies without a Confirm prompt. Sylvan Splendor draws for the
// aura's controller, The Immortal Throne draws for the caster and
// adds a level counter to itself. Prompt-based watchers
// (`animate_target_aura`) still route through `applyCardAutomation`.
export function fireOnSpellCast(host, spellCard) {
  if (!spellCard) return;
  const spellController = spellCard.rotated ? 'p2' : 'p1';
  // A site is an atlas card, not a spell. But per FAQ it IS "playing a
  // card", so global-scope watchers ("Whenever anyone plays a card",
  // The Immortal Throne) must still see it. Sites fire ONLY global
  // entries here; self_controller spell-only watchers (Enchantress,
  // Sylvan Splendor) never react to a site. Sites also stay out of the
  // per-turn cast ledger (they aren't spells).
  const isSite = !!spellCard.isSite;
  if (!isSite) {
    // Per-turn cast history. Every cast funnels through here (hand
    // plays, Archimago / Deathspeaker copies), so "spells you've cast
    // this turn" effects (Sparkmage's (A) sum) read one ledger. Cleared
    // at the end of the caster's turn in turnPhaseRunner.
    if (!host._spellsCastThisTurn) host._spellsCastThisTurn = { p1: [], p2: [] };
    host._spellsCastThisTurn[spellController].push(spellCard.cardId);
  }
  for (const [, mesh] of host.meshes) {
    const carrierCard = mesh?.userData?.cardInstance;
    // Walk the carrier's equipped artifacts too: The Immortal Throne can be
    // a Relic, so once equipped it lives in its bearer's equippedCards with
    // no own mesh. Its on_spell_cast watcher must still fire (and tick).
    const candidates = [];
    if (carrierCard) candidates.push(carrierCard);
    for (const eq of (carrierCard?.equippedCards || [])) candidates.push(eq);
    for (const card of candidates) {
      if (!card || card.id === spellCard.id) continue;
      const fullCard = host._getFullCard(card.cardId);
      const entries = (fullCard?.automation || []).filter(
        (e) => e.trigger === TRIGGER.ON_SPELL_CAST,
      );
      if (entries.length === 0) continue;
      const watcherController = card.rotated ? 'p2' : 'p1';
      for (const entry of entries) {
        const where = entry.scope?.where;
        // self_controller narrows to the watcher's own casts; global
        // fires for any caster. (Other scopes reserved for future use.)
        if (where === 'self_controller' && watcherController !== spellController) continue;
        if (isSite && where !== 'global') continue;
        const condOk = host._evaluateCondition(entry.condition, card, fullCard, {
          watchTarget: spellCard,
        });
        if (!condOk) continue;
        // Inline silent reward (draw / add_counter); falls through to the
        // prompt pipeline for any other effect shape (Enchantress).
        if ((entry.effect || []).every((e) => e.op === OP.DRAW || e.op === OP.ADD_COUNTER)) {
          // Auth cutover: the SERVER fires these reward entries from the
          // storyline MOVE_TO_ZONE command's on_spell_cast fan-out
          // (spellCastTriggers). Skip locally so the draw/counter resolves once;
          // the diff renders the result.
          if (host._authZonesEnabled?.()) continue;
          // Per codex, "draw a spell" on a global-scope watcher rewards
          // the CASTER (The Immortal Throne); a controller-scoped watcher
          // rewards its own controller (Sylvan Splendor).
          const recipient = where === 'global' ? spellController : watcherController;
          for (const eff of (entry.effect || [])) {
            resolveCastWatchEffect(host, card, eff, recipient);
          }
          continue;
        }
        host.applyCardAutomation(card, [TRIGGER.ON_SPELL_CAST], {
          watchTarget: spellCard,
        });
      }
    }
  }
}

// Fire `on_draw` watchers after a card leaves a deck for a hand. The
// single draw choke point (cardPileLifecycle.drawCard) calls this once
// per card drawn. Covering BOTH the mandatory start-phase draw and
// effect-driven draws. `player` is who drew. Watch context is the
// drawing player's Avatar so existing `watch_target_filter`
// (enemy / ally / type_ / near_source) conditions gate without a new
// condition type (Iron Maiden: "a nearby Avatar draws").
//
// Re-entrancy guard: an on_draw effect that itself draws (Queen of
// Midland) would re-enter drawCard → fireOnDraw and recurse forever.
// `host._firingOnDraw` short-circuits any draw made WHILE resolving
// on_draw, so the nested draw still lands the card and bumps the
// per-turn counter but does not itself re-fire the trigger.
//
// Iron Maiden and Chains of Prometheus resolve inline (silent). Their
// effects (lose_life by per-turn draw count, tap the drawer's strongest
// untapped minion) take no player choice, so a Confirm drawer would be
// pure friction and the OTHER client must apply them deterministically.
export function fireOnDraw(host, player) {
  if (!player) return;
  if (host._firingOnDraw) return;
  const drawerAvatar = host._findAvatarCard(player === 'p2');
  host._firingOnDraw = true;
  try {
    for (const [, mesh] of host.meshes) {
      const card = mesh?.userData?.cardInstance;
      if (!card) continue;
      const fullCard = host._getFullCard(card.cardId);
      const entries = (fullCard?.automation || []).filter(
        (e) => e.trigger === TRIGGER.ON_DRAW,
      );
      if (entries.length === 0) continue;
      if (isCardSilenced(card) || isDisabled(card)) continue;
      const watcherController = card.rotated ? 'p2' : 'p1';
      // Entries flagged `optional` (a rules-text "may" that draws for the
      // WATCHER, e.g. Queen of Midland) route through the full automation
      // path: it renders the Confirm/Decline drawer, draws for the
      // watcher's controller via applyEffectsForConfirm, and gates the
      // condition with the host accessors (handCountFor) the inline
      // resolver lacks. No card mixes optional and silent on_draw entries,
      // so routing the whole card is safe; the mandatory silent entries
      // (Iron Maiden, Chains of Prometheus) keep the inline,
      // drawer-targeted resolver below.
      if (entries.some((e) => e.optional)) {
        host.applyCardAutomation(card, [TRIGGER.ON_DRAW], {
          watchTarget: drawerAvatar,
          controller: watcherController,
        });
        continue;
      }
      for (const entry of entries) {
        if (entry.scope?.where === 'self_controller' && watcherController !== player) continue;
        // Auth cutover: the SERVER owns this entry (drawTriggers fan-out.
        // Chains of Prometheus' tap AND Iron Maiden's counter-driven
        // lose_life; the server holds the authoritative cardsDrawnThisTurn).
        // Skip it locally so it resolves once; the fold renders the outcome.
        if (host._authZonesEnabled?.() && serverOwnsOnDrawEntry(entry)) continue;
        const condOk = evaluateCondition(entry.condition, card, fullCard, host.meshes, {
          cellAt: host._cellAtWorld,
          watchTarget: drawerAvatar,
        });
        if (!condOk) continue;
        for (const eff of (entry.effect || [])) {
          resolveOnDrawEffect(host, card, eff, player);
        }
      }
    }
  } finally {
    host._firingOnDraw = false;
  }
}

// Fire `on_tap` watchers after a minion taps. The single tap choke
// point (cardStateOps.applyTap) calls this once per successful tap
// (nextValue === true only. Untaps don't fire). `tappedCard` is the
// minion that tapped; it's the watch context so existing
// `watch_target_filter` conditions (near_source / Minion / ally /
// enemy) gate without a new condition type. False Idol: "Whenever a
// nearby minion taps, its opponent draws a spell."
//
// Re-entrancy guard: an on_tap effect that itself taps (none today,
// but symmetric with fireOnDraw) won't recurse. `host._firingOnTap`
// short-circuits any tap made WHILE resolving on_tap.
//
// All current on_tap effects (draw_for_watch_target_opponent) resolve
// inline (silent). No player choice, and the OTHER client must apply
// them deterministically. Optional / prompt-based on_tap entries would
// route through applyCardAutomation; none exist yet.
export function fireOnTap(host, tappedCard) {
  if (!tappedCard) return;
  if (host._firingOnTap) return;
  host._firingOnTap = true;
  try {
    const tappedController = tappedCard.rotated ? 'p2' : 'p1';
    for (const [, mesh] of host.meshes) {
      const carrierCard = mesh?.userData?.cardInstance;
      // Walk the carrier's equipped artifacts too: False Idol is a Relic,
      // so once equipped it lives in its bearer's equippedCards (no own
      // mesh). Its anchor for near_source proximity is the carrier's cell.
      const carrierCell = host._cellAtWorld(mesh.position.x, mesh.position.z);
      const candidates = [];
      if (carrierCard) candidates.push({ card: carrierCard, anchored: false });
      for (const eq of (carrierCard?.equippedCards || [])) {
        candidates.push({ card: eq, anchored: true });
      }
      for (const { card, anchored } of candidates) {
        if (!card || card.id === tappedCard.id) continue;
        if (isCardSilenced(card) || isDisabled(card)) continue;
        const fullCard = host._getFullCard(card.cardId);
        const entries = (fullCard?.automation || []).filter(
          (e) => e.trigger === TRIGGER.ON_TAP,
        );
        if (entries.length === 0) continue;
        const watcherController = card.rotated ? 'p2' : 'p1';
        for (const entry of entries) {
          if (entry.scope?.where === 'self_controller' && watcherController !== tappedController) continue;
          // Auth cutover: the silent watcher-draw shape (False Idol) fires
          // server-side from the tap fan-out (tapTriggers) at every server tap
          // site. SET_TAPPED command, move auto-tap, combat attack tap. Skip
          // it locally so the draw lands once.
          if (host._authZonesEnabled?.() && (entry.effect || []).length > 0
              && (entry.effect || []).every((e) => e.op === OP.DRAW || e.op === OP.DRAW_FOR_WATCH_TARGET_OPPONENT)) continue;
          // An equipped artifact has no own mesh, so evaluateCondition's
          // near_source mesh lookup against the artifact's id would fail.
          // Anchor proximity to the carrier's cell here and strip
          // near_source from the condition passed to evaluateCondition.
          let condition = entry.condition;
          if (anchored && condition?.near_source === true) {
            const tMesh = host.meshes.get(tappedCard.id);
            const tCell = tMesh ? host._cellAtWorld(tMesh.position.x, tMesh.position.z) : null;
            if (chebyshevDistance(carrierCell, tCell) > 1) continue;
            const { near_source, ...rest } = condition;
            condition = rest;
          }
          const condOk = evaluateCondition(condition, card, fullCard, host.meshes, {
            cellAt: host._cellAtWorld,
            watchTarget: tappedCard,
          });
          if (!condOk) continue;
          for (const eff of (entry.effect || [])) {
            resolveWatcherDrawEffect(host, eff, tappedCard);
          }
        }
      }
    }
  } finally {
    host._firingOnTap = false;
  }
}

// Fire `on_summon_here` watchers when a minion is summoned onto a
// cell. Walks every in-play card with an on_summon_here entry, resolves
// the watcher's anchor cell (own mesh OR, for an equipped Monument /
// Relic, its bearer's cell), and fires entries whose cell matches the
// summon cell. `summonedUnit` is the watch context so
// watch_target_filter (name / name_words / subtype) gates it. The Round
// Table: "Whenever you summon King Arthur, or a Knight, Sir, or Dame to
// The Round Table, draw a card." (scope self_controller → the summoning
// player draws).
export function fireOnSummonHere(host, summonedUnit, cell) {
  if (!summonedUnit || !cell) return;
  // S3. The server's cast commit fires on_summon_here watchers itself; the
  // local inline draw here would double with the server's.
  if (host._authZonesEnabled?.()) return;
  const summonController = summonedUnit.rotated ? 'p2' : 'p1';
  for (const [, mesh] of host.meshes) {
    const watcherDirect = mesh?.userData?.cardInstance;
    // Walk the carrier's equipped Monuments / Relics too: The Round
    // Table is a Monument, so once conjured it lives in a site's
    // equippedCards (no own mesh). Its anchor is the carrier's cell.
    const carrierCard = watcherDirect;
    const carrierCell = host._cellAtWorld(mesh.position.x, mesh.position.z);
    const candidates = [];
    if (watcherDirect) candidates.push({ card: watcherDirect, cell: carrierCell });
    for (const eq of (carrierCard?.equippedCards || [])) {
      candidates.push({ card: eq, cell: carrierCell });
    }
    for (const { card, cell: anchorCell } of candidates) {
      if (!card || card.id === summonedUnit.id) continue;
      if (!anchorCell || anchorCell.col !== cell.col || anchorCell.row !== cell.row) continue;
      if (isCardSilenced(card) || isDisabled(card)) continue;
      const fullCard = host._getFullCard(card.cardId);
      const entries = (fullCard?.automation || []).filter(
        (e) => e.trigger === TRIGGER.ON_SUMMON_HERE,
      );
      if (entries.length === 0) continue;
      const watcherController = card.rotated ? 'p2' : 'p1';
      for (const entry of entries) {
        if (entry.scope?.where === 'self_controller' && watcherController !== summonController) continue;
        const condOk = evaluateCondition(entry.condition, card, fullCard, host.meshes, {
          cellAt: host._cellAtWorld,
          watchTarget: summonedUnit,
        });
        if (!condOk) continue;
        for (const eff of (entry.effect || [])) {
          resolveWatcherDrawEffect(host, eff, summonedUnit, watcherController);
        }
      }
    }
  }
}

// Shared inline resolver for the silent watcher draw ops. `draw` goes
// to `recipientOverride` (the watcher's controller, for self_controller
// "you summon → you draw"); `draw_for_watch_target_opponent` ignores
// the override and routes to the OPPONENT of the watch-target card's
// controller (False Idol's "its opponent draws"). Both funnel through
// host.drawCard so the draw is synced + counted once.
function resolveWatcherDrawEffect(host, eff, watchTarget, recipientOverride = null) {
  if (eff.op === OP.DRAW) {
    const recipient = recipientOverride || (watchTarget.rotated ? 'p2' : 'p1');
    drawForPlayer(host, recipient, eff.args);
    return;
  }
  if (eff.op === OP.DRAW_FOR_WATCH_TARGET_OPPONENT) {
    const owner = watchTarget.rotated ? 'p2' : 'p1';
    const opponent = owner === 'p1' ? 'p2' : 'p1';
    drawForPlayer(host, opponent, eff.args);
  }
}

function drawForPlayer(host, player, args = {}) {
  const n = args?.amount ?? 1;
  const pileName = args?.source === 'site' ? 'Atlas' : 'Spellbook';
  const playerNum = player === 'p2' ? 2 : 1;
  const pile = host.findPileByNameForPlayer(pileName, playerNum);
  if (!pile) return;
  for (let i = 0; i < n; i++) host.drawCard(pile.id, { skipLog: true });
}

// Inline resolver for the on_draw silent ops. lose_life with
// `amount_from: 'cards_drawn_this_turn'` reads the drawer's per-turn
// counter (Iron Maiden); tap_strongest_minion taps the drawer's
// strongest untapped minion via the unified tap primitive (Chains of
// Prometheus). draw routes back through the choke point. The
// re-entrancy guard in fireOnDraw keeps that nested draw from
// re-firing on_draw (Queen of Midland loop safety). Life mutation
// routes through playerResources so it syncs and honours Death's Door.
function resolveOnDrawEffect(host, sourceCard, eff, player) {
  if (eff.op === OP.LOSE_LIFE) {
    const amount = eff.args?.amount_from === 'cards_drawn_this_turn'
      ? (host._cardsDrawnThisTurn?.[player] || 0)
      : (eff.args?.amount || 0);
    if (amount > 0) host.playerResources.loseLife(player, amount, sourceCard.name || 'Iron Maiden');
    return;
  }
  if (eff.op === OP.TAP_STRONGEST_MINION) {
    const tied = strongestUntappedMinionsOf(host, player);
    if (tied.length === 0) return;
    // Power tie: the drawing player CHOOSES which of the tied minions
    // taps (Sorcery "if tied, choose one"), not first-encountered.
    // Open a controller cell-pick over the tied minions' cells; the
    // TAP_PICKED_TIED_MINION resolver taps the chosen one.
    if (tied.length > 1) {
      const validCells = new Set();
      for (const minion of tied) {
        const mesh = host.meshes.get(minion.id);
        if (!mesh) continue;
        const cell = host._cellAtWorld(mesh.position.x, mesh.position.z);
        if (cell) validCells.add(`${cell.col},${cell.row}`);
      }
      if (validCells.size > 1) {
        host._startCellPick(sourceCard, player, OP.TAP_PICKED_TIED_MINION, validCells, {
          description: t('Choose your strongest minion to tap'),
          args: { tiedIds: tied.map((m) => m.id) },
        });
        return;
      }
    }
    const minion = tied[0];
    const mesh = host.meshes.get(minion.id);
    if (mesh) host._applyTap(minion, mesh, true, { source: 'automation' });
    return;
  }
  if (eff.op === OP.DRAW) {
    const n = eff.args?.amount ?? 1;
    const pileName = eff.args?.source === 'site' ? 'Atlas' : 'Spellbook';
    const playerNum = player === 'p2' ? 2 : 1;
    const pile = host.findPileByNameForPlayer(pileName, playerNum);
    if (!pile) return;
    for (let i = 0; i < n; i++) host.drawCard(pile.id, { skipLog: true });
  }
}

// The drawing player's untapped minions tied at the highest effective
// power. Returns [] when the player has none, [one] when there's a clear
// strongest, or the full tied set (2+) when several share the max. The
// on_draw path then lets the controller choose which taps (Sorcery
// "if tied, choose one"), instead of a silent first-encountered pick.
function strongestUntappedMinionsOf(host, player) {
  const rotated = player === 'p2';
  const candidates = [];
  let bestPower = -Infinity;
  for (const [, mesh] of host.meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card || card.tapped || card.rotated !== rotated) continue;
    if (!countsAsMinion(card)) continue;
    const power = effectivePower(card);
    if (power > bestPower) bestPower = power;
    candidates.push({ card, power });
  }
  return candidates.filter((c) => c.power === bestPower).map((c) => c.card);
}

// Inline resolver for the silent on_spell_cast reward ops. `draw` goes
// to `recipient`'s Spellbook (or Atlas for source 'site'); add_counter
// bumps the source card's serializable `_counters` and broadcasts it so
// the next cost-equals-counter check reads the same value on both
// clients.
function resolveCastWatchEffect(host, sourceCard, eff, recipient) {
  if (eff.op === OP.DRAW) {
    const n = eff.args?.amount ?? 1;
    const pileName = eff.args?.source === 'site' ? 'Atlas' : 'Spellbook';
    const playerNum = recipient === 'p2' ? 2 : 1;
    const pile = host.findPileByNameForPlayer(pileName, playerNum);
    if (!pile) return;
    for (let i = 0; i < n; i++) host.drawCard(pile.id, { skipLog: true });
    return;
  }
  if (eff.op === OP.ADD_COUNTER) {
    const amount = eff.args?.amount ?? 1;
    sourceCard._counters = (sourceCard._counters || 0) + amount;
    host._broadcastCardCounters?.(sourceCard.id, sourceCard._counters);
    host._refreshCounterAnchorFor?.(sourceCard.id);
    // The Immortal Throne: "At level 8 or more, an Avatar here alone wins."
    // Re-check the win condition every time the level counter advances.
    if (eff.args?.win_at_level_alone) maybeWinAtLevelAlone(host, sourceCard, eff.args.win_at_level_alone);
  }
}

// State-based win check for The Immortal Throne. At `level`+ counters, if
// exactly one Avatar and no other units share the source site's cell, that
// Avatar's controller wins (reuses the standard declareGameOver plumbing).
function maybeWinAtLevelAlone(host, source, level) {
  if ((source._counters || 0) < level) return;
  // The Throne's cell is its own mesh's, or. When equipped as a Relic.
  // its bearer's (the artifact has no mesh of its own).
  let sm = host.meshes.get(source.id);
  if (!sm) {
    for (const [, m] of host.meshes) {
      if (m?.userData?.cardInstance?.equippedCards?.some((c) => c.id === source.id)) { sm = m; break; }
    }
  }
  const cell = sm && host._cellAtWorld(sm.position.x, sm.position.z);
  if (!cell) return;
  let avatar = null;
  let avatarCount = 0;
  let otherUnits = 0;
  for (const [, m] of host.meshes) {
    const c = m?.userData?.cardInstance;
    if (!c || c.id === source.id) continue;
    const cc = host._cellAtWorld(m.position.x, m.position.z);
    if (!cc || cc.col !== cell.col || cc.row !== cell.row) continue;
    if (c.type === 'Avatar') { avatar = c; avatarCount += 1; }
    else if (countsAsMinion(c)) otherUnits += 1;
  }
  if (avatar && avatarCount === 1 && otherUnits === 0) {
    const winner = avatar.rotated ? 'p2' : 'p1';
    host._declareGameOver(winner, msg('{name}: an Avatar stood alone at level 8', { name: source.name || 'The Immortal Throne' }));
  }
}

export function fireRealmTrigger(host, triggerType, watchTargetCard) {
  if (!triggerType || !watchTargetCard) return;
  const boardSnapshot = [];
  for (const [, mesh] of host.meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card) continue;
    const fullCard = host._getFullCard(card.cardId);
    if (!fullCard) continue;
    boardSnapshot.push({
      card,
      fullCard,
      controller: card.rotated ? 'p2' : 'p1',
    });
  }
  const watchers = findRealmTriggerWatchers(triggerType, boardSnapshot, watchTargetCard.id);
  if (watchers.length === 0) return;

  // v1.78. Collect ALL watcher prompts and push as ONE simultaneous
  // batch. pushSimultaneous handles the codex active-vs-non-active
  // ordering (active goes deeper, non-active on top). Within each
  // group, board-iteration order is the engine's default declaration
  // order. A future priority-pick UI can let the active player
  // reorder. Each watcher's prompts are themselves in author-source
  // order; pushSimultaneous appends in that intent-order so the
  // first author-listed entry of the first-declared watcher ends up
  // deepest, and the LIFO reversal we do at push time only applies
  // to the per-call _pushStorylineEvents path (collected-prompts
  // skip that reversal, which matches codex: the active player
  // declares each event's stack position individually).
  const activePlayer = host.state.currentTurn;
  const { active, nonActive } = partitionByPriority(watchers, activePlayer);
  const collected = [];
  for (const w of [...active, ...nonActive]) {
    // v1.95. Resolve silent watch-target ops inline (no prompt).
    // Druid's "enemy entered nearby → 1 damage" must fire without
    // asking the player to confirm. Entries with NON-silent ops
    // still go through applyCardAutomation → prompt drawer.
    const consumed = host._resolveSilentRealmEntries(w, triggerType, watchTargetCard);
    if (consumed === 'all') continue;
    host.applyCardAutomation(w.card, [triggerType], {
      watchTarget: watchTargetCard,
      collectPrompts: collected,
    });
  }
  host._pushSimultaneousStorylineEvents(collected, activePlayer);
}

// v1.101. Combat watcher firing. Mirror of _fireRealmTrigger but
// for combat events. Walks every card on the board (not just the
// striker / defender. Those self-fire via _fireCombatTrigger) and
// checks for entries whose scope.where indicates a watcher (not
// 'self'). Each watcher's condition is evaluated with the watch
// context (defender as watchTarget) and matching effects fire
// through the normal pipeline. Used by Interrogator-style cards.
export function fireCombatWatcherTrigger(host, triggerType, context = {}) {
  if (!triggerType) return;
  // S3. Server-autonomous combat trigger families resolve from the server's
  // combat manifest (fireGlobalOnDamageTakenOnDraft et al.); the local watcher
  // pass would double-resolve them.
  if (host._authZonesEnabled?.() && SERVER_AUTONOMOUS_TRIGGERS.has(triggerType)) return;
  const watchTarget = context.defender || context.killed || context.source || null;
  if (!watchTarget) return;
  for (const [, mesh] of host.meshes) {
    const watcher = mesh?.userData?.cardInstance;
    if (!watcher) continue;
    // Skip the striker. Self-triggers are handled by _fireCombatTrigger.
    if (context.striker && watcher.id === context.striker.id) continue;
    const fullWatcher = host._getFullCard(watcher.cardId);
    const entries = (fullWatcher?.automation || []).filter((e) => e.trigger === triggerType);
    if (entries.length === 0) continue;
    for (const entry of entries) {
      // Only fire watcher-pattern entries (scope.where != 'self').
      const scopeWhere = entry.scope?.where;
      if (!scopeWhere || scopeWhere === 'self') continue;
      if (entry.condition) {
        const ok = evaluateCondition(entry.condition, watcher, fullWatcher, host.meshes,
          { cellAt: host._cellAtWorld, watchTarget, amount: context.amount });
        if (!ok) continue;
      }
      // All-silent entries (Interrogator's dodge prompt) resolve inline
      // through the same dispatcher as the self path. The generic
      // prompt builder has no renderer for these ops.
      const allSilent = (entry.effect || []).every((e) => COMBAT_SILENT_OPS.has(e.op));
      if (allSilent) {
        host._resolveCombatSilentEffects(watcher, entry, context, watchTarget);
        continue;
      }
      host.applyCardAutomation(watcher, [triggerType], { watchTarget: context });
    }
  }
}

// v1.95. Fire any entries on `watcher` whose effects are all
// silent-resolvable (DAMAGE_WATCH_TARGET today; future: KILL_,
// BANISH_, RETURN_WATCH_TARGET) inline against the watch context.
// Returns 'all' if EVERY matching entry was silent-handled (caller
// skips the prompt pipeline), 'none' otherwise.
export function resolveSilentRealmEntries(host, watcher, triggerType, watchTargetCard) {
  // Silenced/disabled watchers fire nothing. 'all' tells the caller the
  // pipeline has nothing left to do for this watcher.
  if (isCardSilenced(watcher.card) || isDisabled(watcher.card)) return 'all';
  const entries = (watcher.fullCard?.automation || []).filter((e) => e.trigger === triggerType);
  if (entries.length === 0) return 'all';
  const SILENT_OPS = new Set([OP.DAMAGE_WATCH_TARGET]);
  const allSilent = entries.every((e) => (e.effect || []).every((eff) => SILENT_OPS.has(eff.op)));
  if (!allSilent) return 'none';
  for (const entry of entries) {
    if (entry.condition) {
      const ok = evaluateCondition(entry.condition, watcher.card,
        watcher.fullCard, host.meshes,
        { cellAt: host._cellAtWorld, watchTarget: watchTargetCard });
      if (!ok) continue;
    }
    for (const eff of (entry.effect || [])) {
      if (eff.op === OP.DAMAGE_WATCH_TARGET) {
        const amount = eff.args?.amount || 0;
        if (amount > 0) {
          host._applyWatchTargetDamage(watchTargetCard, watcher.card, amount);
        }
      }
    }
  }
  return 'all';
}

// Damage routing for a watch-trigger context (Druid's enter-here
// damage, Bottomless Pit-style entry effects, etc.). Goes through
// the unified `_applyDamageToTarget` so the Avatar/Site/Minion
// dispatch + minion-HP write share one recipe with the main spell
// damage path. Watch triggers DO log + sweep. They're treated as
// independent damage events for codex purposes.
export function applyWatchTargetDamage(host, target, source, amount) {
  host._applyDamageToTarget(target, amount, {
    sourceLabel: source?.name || 'Source',
    log: true,
    sweep: true,
    floatingDamage: true,
  });
}

// Fire on_death automation (Deathrite) BEFORE the card leaves the
// table. Called from sendCardToPile when the destination is Cemetery
// or Banished. Both count as "death" for triggered abilities.
// Returning to hand or Atlas/Spellbook does NOT fire Deathrite.
// The source's mesh is still in this.meshes here so scope resolution
// (adjacent / nearby) finds the right anchor.
export function fireDeathriteOnLeave(host, cardInstance, destinationPileName) {
  if (destinationPileName !== 'Cemetery' && destinationPileName !== 'Banished') return;
  const fullCard = host._getFullCard(cardInstance.cardId);
  if (!fullCard?.automation?.length) return;
  if (!fullCard.automation.some((e) => e.trigger === 'on_death')) return;
  // Snapshot the source's position NOW. The mesh is about to leave
  // host.meshes, so by the time the player confirms the Deathrite
  // prompt scope resolution would otherwise fail. Same casterOverrides
  // mechanism the v1.14 spell flow uses.
  const mesh = host.meshes.get(cardInstance.id);
  const sourcePos = mesh ? { x: mesh.position.x, z: mesh.position.z } : null;
  host.applyCardAutomation(cardInstance, ['on_death'], {
    casterOverrides: sourcePos
      ? { sourcePos, controllerRotated: cardInstance.rotated, casterId: cardInstance.id }
      : null,
  });
}

// Shared cleanup for any card leaving the table. Ledger entries
// removed by source, affinities recalced, aura board rebuilt. Called
// from removeCardFromTable AND sendCardToPile's animated branch
// (which used to skip the ledger sweep, leaving Castle Servants'
// (A) threshold stuck on the controller's tracker after banish/kill
// V1.15b fix).
export function disposeCardEffectsOnLeave(host, cardInstance, broadcast = true) {
  if (host.effectLedger.entries.some((e) => e.sourceCardId === cardInstance.id)) {
    // v1.20. Symmetric mana drop for any GRANT_MANA contribution
    // (artifacts like Onyx Core, sites with extra mana grants). Must
    // run BEFORE removeBySource so the entries are still readable.
    host._reverseManaContribution(cardInstance.id);
    host.effectLedger.removeBySource(cardInstance.id);
    host.recalculateSiteAffinities('p1');
    host.recalculateSiteAffinities('p2');
  }
  // v1.28. Single unified leave-play reset. Wipes every transient
  // per-instance field (statuses, aura buffs, manual stat edits,
  // ward grants, control claims, tapped / faceDown, equipment,
  // group, interaction order). When the card later re-enters the
  // realm (from cemetery / hand / atlas / etc.), addCardToTable
  // re-initialises currentAttack/Life from baseAttack/Life and the
  // automation pipeline grants printed keywords fresh.
  resetCardOnLeavePlay(cardInstance);
  // The departing card may have been an aura source OR a target;
  // either way the board state changed.
  host.recomputeStaticAuras();
}

export function recalculateSiteAffinities(host, player) {
  // Auth path (W3): the SERVER derives players[seat].thresholds on every commit
  // (auraDerive applyPoolTotals) and the resources fold renders them verbatim
  // into the trackers. Computing + asserting them here made the client a
  // second writer (the SET_THRESHOLDS ping-pong). No-op so the many call sites
  // stay untouched; sandbox / legacy matches keep the local computation.
  if (host._authZonesEnabled?.()) return;
  // No/unknown seat → recompute BOTH. The auth ledger/card-state folds call
  // this argless (a server-minted ledger entry can affect either seat), and
  // `trackers[undefined][key]` used to throw here. Killing the rest of the
  // diff fold mid-flight (the played-card "bounce back to hand" bug).
  if (player !== 'p1' && player !== 'p2') {
    recalculateSiteAffinities(host, 'p1');
    recalculateSiteAffinities(host, 'p2');
    return;
  }
  const playerIsP2 = player === 'p2';
  const totals = { earth: 0, water: 0, fire: 0, wind: 0 };

  for (const [, mesh] of host.meshes) {
    const card = mesh.userData.cardInstance;
    if (!card || !card.isSite) continue;
    if (card.rotated !== playerIsP2) continue;

    const fullCard = host._getFullCard(card.cardId);
    if (!fullCard) continue;

    // Single source of truth: per-site effective thresholds (printed
    // ∘ multiplier ∘ suppression ∘ flooded). Without this the tracker
    // would ignore Atlantean Fate's "only provide Water" suppression
    // and Sow the Earth's 2× multiplier, even though the playmat
    // affinity pips (driven by elementsAtCell) already reflect them.
    const eff = effectiveSiteThresholds(card, fullCard);
    totals.earth += eff.earth;
    totals.water += eff.water;
    totals.fire  += eff.fire;
    totals.wind  += eff.air; // card data uses "air", tracker uses "wind"
  }

  // Add temporary threshold grants from the effect ledger
  // (e.g. Bloom sites' "Provides X this turn" entries).
  const ledgerContrib = host.effectLedger.thresholdContribution(player);
  totals.earth += ledgerContrib.earth;
  totals.water += ledgerContrib.water;
  totals.fire  += ledgerContrib.fire;
  totals.wind  += ledgerContrib.air; // ledger uses 'air'; tracker uses 'wind'

  const { trackers } = host.state.gameState;
  if (!trackers?.[player]) return; // trackers not seeded yet (early fold)
  let changed = false;
  for (const [key, value] of Object.entries(totals)) {
    if (trackers[player][key] !== value) {
      trackers[player][key] = value;
      host._broadcastTracker(player, key, value);
      changed = true;
    }
  }
  if (changed) {
    host.updateTrackerTokenPositions();
    host.forceUpdate();
  }
}

// Count only mana-producing sites (excludes Rubble and similar).
export function countSitesForPlayer(host, player) {
  const playerIsP2 = player === 'p2';
  let count = 0;
  for (const [, mesh] of host.meshes) {
    const card = mesh.userData.cardInstance;
    if (card && card.isSite && card.rotated === playerIsP2 && host._siteProvidesMana(card)) count++;
  }
  return count;
}
